<script setup>
import { ref, onMounted, onUnmounted } from 'vue';

const suggestions = ref([]);
const loading = ref(true);

const specialistMeta = {
    stella: { bgClass: 'bg-aiAgentWebDesigner/40', borderClass: 'border-aiAgentWebDesigner', iconColor: 'text-gray-800', buttonClass: 'bg-aiAgentWebDesigner hover:opacity-90' },
    maya: { bgClass: 'bg-aiAgentContentCreator/40', borderClass: 'border-aiAgentContentCreator', iconColor: 'text-gray-800', buttonClass: 'bg-aiAgentContentCreator hover:opacity-90' },
    susan: { bgClass: 'bg-aiAgentDataAnalyst/40', borderClass: 'border-aiAgentDataAnalyst', iconColor: 'text-gray-800', buttonClass: 'bg-aiAgentDataAnalyst hover:opacity-90' },
    tom: { bgClass: 'bg-aiAgentRelationshipManager/40', borderClass: 'border-aiAgentRelationshipManager', iconColor: 'text-gray-800', buttonClass: 'bg-aiAgentRelationshipManager hover:opacity-90' },
    oscar: { bgClass: 'bg-aiAgentLeadFinder/40', borderClass: 'border-aiAgentLeadFinder', iconColor: 'text-gray-800', buttonClass: 'bg-aiAgentLeadFinder hover:opacity-90' },
};

const defaultMeta = { bgClass: 'bg-gray-50', borderClass: 'border-gray-200', iconColor: 'text-gray-600', buttonClass: 'bg-gray-500 hover:bg-gray-600' };

const specialistAvatarsComponentNames = {
    stella: 'ai-agent-web-designer-avatar',
    maya: 'ai-agent-content-creator-avatar',
    susan: 'ai-agent-data-analyst-avatar',
    tom: 'ai-agent-relationship-manager-avatar',
    oscar: 'ai-agent-lead-finder-avatar',
}

function getMeta(specialist) {
    return specialist ? (specialistMeta[specialist] || defaultMeta) : defaultMeta;
}

function getAvatar(specialist) {
    return specialist ? specialistAvatarsComponentNames[specialist] : null;
}

function getSuggestionMessage(suggestion) {
    const key = 'dashboard:quickActions.' + suggestion.key + '.message';

    if (suggestion.key !== 'inactive_specialist' || ! suggestion.specialist) {
        return _mft(key);
    }

    return _mft(key, {
        name: _mft('dashboard:aiSpecialists.' + suggestion.specialist + '.name'),
    });
}

async function fetchSuggestions() {
    try {
        const response = await axios.get('/bo/ai-suggestions');
        suggestions.value = response.data;
    } catch (e) {
        // Silently fail
    } finally {
        loading.value = false;
    }
}

function handleAction(suggestion) {
    if (suggestion.key === 'empty_specialist_slots') {
        window.dispatchEvent(new CustomEvent('open-dashboard-hiring-modal'));
    } else if (suggestion.action_type === 'link' && suggestion.action_url) {
        window.location.href = suggestion.action_url;
    } else if (suggestion.action_type === 'hire_modal') {
        window.dispatchEvent(new CustomEvent('open-dashboard-hiring-modal'));
    } else if (suggestion.action_type === 'onboarding') {
        // Trigger the business context onboarding
        window.dispatchEvent(new CustomEvent('open-business-context-onboarding'));
    } else if (suggestion.action_type === 'scroll_to_schedules') {
        window.dispatchEvent(new CustomEvent('open-work-schedule-modal'));
    }
}

function displayActionButton(suggestion) {
    return Boolean(suggestion.action_type || suggestion.action_url);
}

function removeEmptySpecialistSlotsSuggestion() {
    suggestions.value = suggestions.value.filter(suggestion => suggestion.key !== 'empty_specialist_slots');
}

function handleTeamUpdated(event) {
    if (Number(event.detail?.remaining_slots) <= 0) {
        removeEmptySpecialistSlotsSuggestion();
    }

    fetchSuggestions();
}

onMounted(() => {
    window.addEventListener('dashboard-ai-team-updated', handleTeamUpdated);
    fetchSuggestions();
});

onUnmounted(() => {
    window.removeEventListener('dashboard-ai-team-updated', handleTeamUpdated);
});
</script>

<template>
    <div v-if="!loading && suggestions.length > 0" class="mb-4">
        <div class="space-y-4">
            <card-basic
                v-for="suggestion in suggestions"
                :key="suggestion.key"
                role="secondary"
            >
                <div class="flex items-center justify-between">
                    <div class="flex items-center gap-3">
                        <!-- Specialist icon dot -->
                        <div
                            class="flex items-center justify-center flex-shrink-0 w-8 h-8 rounded-full"
                            :class="getMeta(suggestion.specialist).bgClass"
                        >
                            <component v-if="getAvatar(suggestion.specialist)" :is="getAvatar(suggestion.specialist)" class="w-8 h-8" />
                            <svg
                                v-if="! getAvatar(suggestion.specialist)"
                                class="w-4 h-4"
                                :class="getMeta(suggestion.specialist).iconColor"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                stroke-width="2"
                                stroke-linecap="round"
                                stroke-linejoin="round"
                            >
                                <path v-if="suggestion.key === 'no_business_context'" d="M12 20h9M16.5 3.5a2.121 2.121 0 1 1 3 3L7 19l-4 1 1-4L16.5 3.5Z" />
                                <path v-else-if="suggestion.key === 'empty_specialist_slots'" d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2M12 3a4 4 0 1 0 0 8M19 8v6M22 11h-6" />
                                <path v-else-if="suggestion.key === 'no_landing_pages'" d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8l-6-6ZM14 2v6h6M12 18v-6M9 15h6" />
                                <path v-else-if="suggestion.key === 'no_content_pieces'" d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8l-6-6ZM14 2v6h6M8 13h8M8 17h4" />
                                <path v-else-if="suggestion.key === 'inactive_specialist'" d="M12 8v4l3 3M3 12a9 9 0 1 0 18 0 9 9 0 0 0-18 0Z" />
                                <path v-else-if="suggestion.key === 'no_work_schedules'" d="M8 2v4M16 2v4M3 10h18M5 4h14a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2Z" />
                                <path v-else d="M13 2L3 14h9l-1 8 10-12h-9l1-8Z" />
                            </svg>
                        </div>

                        <span class="text-sm text-gray-700">
                            {{ getSuggestionMessage(suggestion) }}
                        </span>
                    </div>

                    <button-primary
                        v-if="displayActionButton(suggestion)"
                        size="sm"
                        class="flex-shrink-0"
                        @click="handleAction(suggestion)"
                    >
                        {{ _mft('dashboard:quickActions.' + suggestion.key + '.action') }}
                    </button-primary>
                </div>
            </card-basic>
        </div>
    </div>
</template>
